// src/pages/CheckoutLinksPage.jsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Eye, Copy, Pencil, Trash2, Plus, ShoppingCart } from "lucide-react";
import { toast } from "react-toastify";
import ConfirmDialog from "../components/ConfirmDialog";
import ModalCheckouts from "../components/ui/ModalCheckouts";
import axios from "axios";
import { getAuthHeaders } from "../services/authHeaders";
import { getCheckoutLinks, deleteCheckoutLink } from "../services/checkoutLinks";

const fmt = (d) => { try { return format(new Date(d), "dd/MM/yyyy"); } catch { return "—"; } };

async function fetchCheckouts(uuid) {
  const { data } = await axios.get(
    `https://api.sobevenda.com.br/api-proxy/affiliate/checkout-events-by-link?uuid=${uuid}`,
    { headers: getAuthHeaders() }
  );
  return data;
}

// ─── URL Cell ─────────────────────────────────────────────────────────────────

function UrlCell({ url, platform }) {
  if (!url) return <span className="text-slate-400">—</span>;
  const isBuygoods = platform?.toLowerCase() === "buygoods";
  const fullCopyText = isBuygoods ? `<script src="${url}"></script>` : url;
  const preview = url.length > 70 ? `${url.slice(0, 54)}…${url.slice(-12)}` : url;
  const copy = () => { navigator.clipboard.writeText(fullCopyText); toast.success("URL copiada!"); };

  return (
    <div className="flex items-center gap-2">
      <span title={fullCopyText} onClick={copy}
        className="cursor-pointer truncate max-w-[400px] font-mono text-xs text-slate-600 dark:text-slate-300 hover:underline">
        {preview}
      </span>
      <button type="button" onClick={copy} title="Copiar URL"
        className="p-1 rounded text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 shrink-0">
        <Copy className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function CheckoutLinksPage() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [modalCheckoutsOpen, setModalCheckoutsOpen] = useState(false);
  const [modalCheckoutLink, setModalCheckoutLink] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const data = await getCheckoutLinks();
        setLinks(Array.isArray(data) ? data : []);
      } catch { toast.error("Não foi possível carregar os links"); setLinks([]); }
      finally { setLoading(false); }
    })();
  }, []);

  const handleDeleteConfirmed = async () => {
    const id = deleteId;
    setDeleteId(null);
    try {
      await deleteCheckoutLink(id);
      setLinks((l) => l.filter((x) => x.id !== id));
      toast.success("Link excluído");
    } catch { toast.error("Falha ao excluir"); }
  };

  return (
    <div className="p-4 md:p-6">
      <div className="space-y-6">

        {/* HEADER */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <ShoppingCart className="w-6 h-6" /> Link de Checkout
            </h1>
            <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
              Gerencie seus links de checkout por plataforma.
            </p>
          </div>
          <button onClick={() => navigate("/checkout-links/new")}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100 shrink-0">
            <Plus className="w-4 h-4" /> Criar novo
          </button>
        </div>

        {/* Mobile: Cards */}
        <div className="lg:hidden space-y-3">
          {loading ? (
            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 text-sm text-slate-500">Carregando…</div>
          ) : links.length === 0 ? (
            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 text-center text-sm text-slate-500">Nenhum link cadastrado.</div>
          ) : links.map((l) => (
            <div key={l.id} className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <div className="text-sm font-bold text-slate-900 dark:text-slate-100">{l.name || "—"}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{l.platform || "—"} · {fmt(l.created_at)}</div>
                </div>
                <span className="text-xs text-slate-500">
                  {typeof l.qtd_checkouts === "number" ? l.qtd_checkouts : 0} checkouts
                </span>
              </div>
              <div className="mb-3"><UrlCell url={l.checkout_link} platform={l.platform} /></div>
              <div className="flex gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
                <button onClick={() => { setModalCheckoutLink(l); setModalCheckoutsOpen(true); }}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-xs">
                  <Eye className="w-3.5 h-3.5" /> Ver
                </button>
                <button onClick={() => navigate(`/checkout-links/${l.id}/edit`)}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-xs">
                  <Pencil className="w-3.5 h-3.5" /> Editar
                </button>
                <button onClick={() => setDeleteId(l.id)}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-red-200 dark:border-red-900 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 text-xs">
                  <Trash2 className="w-3.5 h-3.5" /> Excluir
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop: Tabela */}
        <div className="hidden lg:block rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
          <table className="min-w-full table-auto text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/40 border-b border-slate-200 dark:border-slate-700">
              <tr className="text-left">
                {["Data Criação","Nome","Plataforma","URL","Checkouts","Ações"].map((h, i) => (
                  <th key={h} className={`px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-300 ${i >= 4 ? "text-center" : ""}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {loading ? (
                <>
                  {[1,2,3].map((i) => (
                    <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                      {Array.from({length:6}).map((_,j) => (
                        <td key={j} className="px-4 py-3">
                          <div className="h-4 w-full max-w-[10rem] animate-pulse rounded bg-slate-100 dark:bg-slate-800" />
                        </td>
                      ))}
                    </tr>
                  ))}
                </>
              ) : links.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-slate-500">Nenhum link cadastrado.</td></tr>
              ) : links.map((l) => (
                <tr key={l.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{fmt(l.created_at)}</td>
                  <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">{l.name || "—"}</td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{l.platform || "—"}</td>
                  <td className="px-4 py-3"><UrlCell url={l.checkout_link} platform={l.platform} /></td>
                  <td className="px-4 py-3 text-center text-slate-600 dark:text-slate-300">
                    {typeof l.qtd_checkouts === "number" ? l.qtd_checkouts : 0}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2">
                      <button onClick={() => { setModalCheckoutLink(l); setModalCheckoutsOpen(true); }} title="Ver checkouts"
                        className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button onClick={() => navigate(`/checkout-links/${l.id}/edit`)} title="Editar"
                        className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => setDeleteId(l.id)} title="Excluir"
                        className="p-2 rounded-lg border border-red-200 dark:border-red-900 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmDialog
        open={deleteId !== null}
        title="Excluir link de checkout"
        message="Tem certeza que deseja excluir este link de checkout?"
        confirmLabel="Excluir"
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setDeleteId(null)}
      />

      <ModalCheckouts
        open={modalCheckoutsOpen}
        onClose={() => setModalCheckoutsOpen(false)}
        checkoutLink={modalCheckoutLink}
        fetchCheckouts={fetchCheckouts}
      />
    </div>
  );
}